import * as Phone            from 'libphonenumber-js';
import assert                from '../assert';
import autosize              from 'autosize';
import classnames            from 'classnames';
import F7Event               from '../app/events/f7_event';
import Navbar                from '../components/navbar';
import React, { useState }   from 'react';
import ReactDOM              from 'react-dom';
import ReactPasswordStrength from 'react-password-strength/dist/universal';


//  Use this to create a page for editing an item.  Navigation will include a
//  Back button, and a Save button that lights up when form inputs change, and
//  the form is valid.
//
//  record  — The initial input field values (defaults to {})
//  title   - Title for navigation bar
//  save    - Called with form field values when user clicks the Save button
//            (optional, Save button not rendered without this callback)
//
//  EditPage has three stages:
//
//  - Initially, without a record, only the back button is enabled
//  - Once a record is set, it defaults to view mode (readOnly, !isEditing), and
//    both Back and Edit buttons are enabled
//  - In edit mode, the Cancel and Save buttons are enabled, isEditing,
//    !readOnly
//
//  It renders the child element/function with all the fields necessary to
//  render a form
//
//  The children is a component rendered with a the edit page properties.
//  Typically, a Form.
//
//  For example:
//
//    return (
//      <Forms.EditPage initial={person} title={person.name} save={onSave}>
//        <Forms.Form>
//          ....
//        </Forms.Form>
//      </Forms.EditPage>
//    );
//
//  Or if you need a component between EditPage and Form:
//
//    function MyForm({ ...editPage }) {
//      return (
//        <Forms.Form {...editPage}>
//          ...
//        </Forms.Form>
//      );
//    }
export class EditPage extends React.PureComponent {

  constructor(props) {
    super(props);

    const { record } = this.props;

    this.state = {
      initial: record,
      record,
      isValid: true
    };

    this.onEdit     = this.onEdit.bind(this);
    this.onCancel   = this.onCancel.bind(this);
    this.onSave     = this.onSave.bind(this);
    this.update     = this.update.bind(this);
    this.setSubmit  = this.setSubmit.bind(this);
    this.setFormRef = this.setFormRef.bind(this);
    this.save       = this.save.bind(this);
    this.submit     = null;
    this.form       = null;
  }

  static getDerivedStateFromProps(props, state) {
    const { record }    = props;
    const { isEditing } = state;

    if (record && !isEditing) {
      return {
        initial: record,
        record,
        formID:  newFormID(record)
      };
    } else
      return null;
  }

  onEdit() {
    this.setState({ isEditing: true });
    this.cleanErrorFormIfNeeded();
  }

  onCancel() {
    const { initial } = this.state;
    this.setState({
      initial,
      record:    initial,
      isEditing: false,
      isValid:   true
    });
    this.cleanErrorFormIfNeeded();
  }

  onSubmit(callback) {
    callback();
  }

  update({ record, isValid }) {
    this.setState({ record, isValid });
  }

  cleanErrorFormIfNeeded() {
    if (this.form)
      this.form.cleanError();
  }

  setSubmit(callback) {
    this.submit = callback;
  }

  onSave() {
    if (this.submit)
      this.submit(new Event('submit'));
  }

  setFormRef(ref) {
    this.form = ref;
  }

  async save() {
    const { isValid } = this.state;
    if (isValid) {
      const { record } = this.state;
      await this.props.save(record, { showDefaultErrorUI: false });
      this.setState({
        initial:   record,
        isEditing: false
      });
    }
  }

  render() {
    const { title }                     = this.props;
    const { validate }                  = this.props;
    const { record, isEditing, formID } = this.state;
    const editPage                      = {
      record,
      validate,
      isEditing,
      readOnly:  !isEditing,
      update:    this.update,
      save:      this.save,
      setSubmit: this.setSubmit,
      onRef:     this.setFormRef,
      formID
    };

    const canEdit = !!record;

    const { children } = this.props;
    return (
      <React.Fragment>
        <Navbar title={title}>
          {isEditing && (
            <Navbar.LeftButton label="Cancel" onClick={this.onCancel}/>
          )}
          {isEditing && (
            <Navbar.SaveButton label="Save" form={formID}/>
          )}
          {canEdit && !isEditing && (
            <Navbar.RightButton label="Edit" onClick={this.onEdit}/>
          )}
        </Navbar>
        {children instanceof Function ?
          children(editPage) :
          React.Children.map(children, child => child && React.cloneElement(child, editPage))
        }
      </React.Fragment>
    );
  }
}


//  Base class for dynamic form that tracks validation and changed fields.
//
//  record     - Current record, used in combination with update method
//  readOnly   - Form is not editable, input fields should be read-only, buttons
//               disabled
//  validate   - Called with form fields to validate form before calling save;
//              throw an error if the form is not valid and should not save
//  update     - Called after every change to the value of a field, with the new
//              record, and isValid and isModified flags
//  save       - Called when form submitted, with the record; throw an error
//               if the form is not valid
//  setSubmit  - Enable call form submit from other component
//
//  Field validation looks at one field at a time (eg name not empty, email
//  contains @).  This validation triggers as the user is editing the field.
//
//  Form validation looks at combination of fields (eg form requires either
//  email or phone number).  This validation only occurs when the user tries to
//  submit the form.
//
//  In addition, saving itself may trigger a validation error (eg authenticate
//  user).
//
//
//  To use:
//
//    return (
//      <Forms.Form record={person} save={save}>
//        <FormFields />
//      </Forms.Form>
//    );
//
//    function FormFields({ ...form }) {
//      return (
//        <div className="list block inset">
//          <ul>
//            <Forms.Input  {...form} label="Name" name="name" />
//            <Forms.Button {...form} label="Save" />
//          </ul>
//        </div>
//      );
//    }
export class Form extends React.PureComponent {

  constructor(props) {
    super(props);
    this.state    = this._initialState();
    this.onChange = this.onChange.bind(this);
    this.onBlur   = this.onBlur.bind(this);
    this.onSubmit = this.onSubmit.bind(this);

    if (props.setSubmit)
      props.setSubmit(this.onSubmit.bind(this));
  }

  _initialState() {
    const { record } = this.props;
    const initial    = recordToFields(record || {});
    return {
      initial,
      fields:     initial,
      isModified: false,
      isValid:    true
    };
  }

  componentDidMount() {
    this.form = ReactDOM.findDOMNode(this); // eslint-disable-line react/no-find-dom-node
    if (this.props.onRef)
      this.props.onRef(this);
  }

  componentWillUnmount() {
    this.form = null;
    if (this.props.onRef)
      this.props.onRef(undefined);
  }

  static getDerivedStateFromProps(props, state) {
    const hasChangedRecord = JSON.stringify(props.record) !== JSON.stringify(state.record);
    if (hasChangedRecord) {
      const { record } = props;
      const fields     = recordToFields(record);
      return { fields, record };
    } else
      return null;
  }

  onChange({ target }) {
    clearErrorMessages(target);

    const { name, type } = target;
    const value          = (type === 'checkbox') ? target.checked : target.value;
    const fields         = { ...this.state.fields, [name]: value };
    const isValid        = this.isValid();
    const isModified     = this.isModified(fields);

    this.setState({ fields, isValid, isModified, error: null });

    const update = this.props.update;
    if (update) {
      const record = fieldsToRecord(fields);
      update({ record, isValid, isModified });
    }
  }

  onBlur(event) {
    event.preventDefault();

    const { target } = event;
    target.dispatchEvent(new F7Event(function(app) {
      app.input.validate(target);
    }));
  }

  isModified(fields) {
    const { initial } = this.state;
    return [ ...Object.entries(fields) ].some(([ name, value ]) => value !== initial[name]);
  }

  isValid() {
    return this.form && this.form.checkValidity ? this.form.checkValidity() : true;
  }

  async onSubmit(event) {
    event.preventDefault();

    const { onSaveError } = this.props;
    const isValid         = this.isValid();

    if (isValid) {
      const { fields }   = this.state;
      const { save }     = this.props;
      const { validate } = this.props;

      try {
        if (validate)
          validate(fields);
      } catch (error) {
        this.setState({ error: getErrorMessage(error) });
        return;
      }

      try {
        if (save)
          await save(fieldsToRecord(fields), { showDefaultErrorUI: false });

        const stillMounted = !!this.form;
        if (stillMounted)
          this.setState(this._initialState());

      } catch (error) {
        if (onSaveError)
          onSaveError(error);
        else
          this.setState({ error: getErrorMessage(error) });
      }
    } else
      this.reportFormErrors();
  }

  reportFormErrors() {
    const fields = Array.from(this.form.querySelectorAll('input,textarea,select'));
    this.form.dispatchEvent(new F7Event(function(app) {
      fields.forEach(field => app.input.validate(field));
    }));
  }

  cleanError() {
    this.setState({ error: null });
    Array.from(this.form.querySelectorAll('.input-invalid'))
      .map(clearErrorMessages);
  }

  render() {
    const form = {
      get:        name => this.state.fields[name],
      readOnly:   this.props.readOnly,
      isValid:    this.state.isValid,
      isModified: this.state.isModified,
      error:      this.state.error,
      onBlur:     this.onBlur,
      onChange:   this.onChange
    };

    return (
      <form onSubmit={this.onSubmit} id={this.props.formID} noValidate>
        {React.Children.map(this.props.children, child => (child && React.cloneElement(child, form)))}
      </form>
    );
  }

}


// { name: { full: 'Assaf' } } => { 'name.full': 'Assaf' }
export function recordToFields(record = {}, fields = {}, prefix = '') {
  for (const [ name, value ] of Object.entries(record)) {
    if (value && typeof value === 'object')
      recordToFields(value, fields, `${prefix}${name}.`);
    else
      fields[`${prefix}${name}`] = value; // eslint-disable-line no-param-reassign
  }
  return fields;
}


// { 'name.full': 'Assaf' } => { name: { full: 'Assaf' } }
export function fieldsToRecord(fields, record = {}) {
  for (const [ name, value ] of Object.entries(fields)) {
    const nameParts  = name.split('.');
    const fieldName  = nameParts.pop();
    const insertInto = nameParts.reduce((object, part) => {
      object[part] = object[part] || {}; // eslint-disable-line no-param-reassign
      return object[part];
    }, record);
    insertInto[fieldName] = value; // eslint-disable-line no-param-reassign
  }
  return record;
}


export class FormError {
  constructor(message, errors) {
    this.name      = 'FormError';
    this.message   = message;
    this.errorList = errors;
    this.prototype = Object.create(Error.prototype);
  }
}


function getErrorMessage(error) {
  const isValidationError = error.body?.message === 'Validation Failed';
  const isFormError       = error instanceof FormError;

  if (isValidationError) {
    const { errors } = error.body;
    const message    = errors && errors.length > 0 && (errors[0].message || error.message);
    return message;
  } else if (isFormError)
    return error.errorList;
  else
    return error.friendlyMessage || error.message;
}


// Input field which accepts text input.
//
// name       - Field name
// type       - Field type
// label      - Label text
// required   - True if this field is required
// ...form    - All other properties from the form
export function Input({ get, name, type = 'text', label, required = false, defaultValue, onBlur, onChange, assistType, autoComplete, placeholder, readOnly }) {
  const value = get && get(name) || defaultValue || '';

  function formatValue(val) {
    return inputAssist({ assistType, type, value: val });
  }

  function handleChange(event) {
    const { target }     = event;
    const currentValue   = target.value;
    const formattedValue = formatValue(currentValue);
    const currentRange   = [ target.selectionStart, target.selectionEnd ];
    const newRange       = currentRange.map(position => getOffsetPosition(position, currentValue, formattedValue));

    target.value        = formattedValue;
    target.defaultValue = formattedValue;

    // setSelectionRange fails for input type=email and type=number
    if (![ 'email', 'number' ].includes(type))
      target.setSelectionRange(...newRange);

    onChange(event);
  }

  return (
    <li className="item-content item-input">
      <div className="item-inner">
        <label className="item-title item-label">
          {label}
          <div className="item-input-wrap">
            <input
              name={name}
              type={type}
              value={formatValue(value)}
              required={required}
              placeholder={readOnly ? '' : placeholder}
              readOnly={readOnly}
              onBlur={onBlur}
              onChange={handleChange}
              onInput={handleChange}
              autoComplete={autoComplete}
            />
          </div>
        </label>
      </div>
    </li>
  );
}

export function getOffsetPosition(position, oldValue, newValue) {
  const offset           = newValue.length - oldValue.length;
  const modifiedPosition = position + offset;
  // ensure that position is >= 0 and <= newValue.length
  return clamp(modifiedPosition, 0, newValue.length);
}

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

export class Textarea extends React.PureComponent {

  constructor(props) {
    super(props);
    this.textarea = React.createRef();
  }

  componentDidMount() {
    if (this.props.resizable)
      autosize(this.textarea.current);
  }

  render() {
    const { get }         = this.props;
    const { name }        = this.props;
    const { label }       = this.props;
    const { required }    = this.props;
    const { onBlur }      = this.props;
    const { onChange }    = this.props;
    const { placeholder } = this.props;
    const { readOnly }    = this.props;
    const { className }   = this.props;
    const value           = get && get(name) || '';

    return (
      <li className="item-content item-input">
        <div className="item-inner">
          <label className="item-title item-label">
            {label}
            <div className="item-input-wrap">
              <textarea
                name={name}
                required={required}
                placeholder={readOnly ? '' : placeholder}
                readOnly={readOnly}
                onBlur={onBlur}
                onChange={onChange}
                onInput={onChange}
                value={value}
                ref={this.textarea}
                className={className}
              />
            </div>
          </label>
        </div>
      </li>
    );
  }
}

Textarea.defaultProps = {
  required:  true,
  resizable: true
};


// Password strength indicator input
//
// name       - Field name
// label      - Label text
// required   - True if this field is required
// ...form    - All other properties from the form
export function PasswordStrengthInput({ name, type = 'password', label, required = false, onBlur, onChange, assistType, placeholder, readOnly }) {
  function handleChange(event) {
    event.target.value = inputAssist({ assistType, value: event.target.value }); // eslint-disable-line
    onChange(event);
  }

  return (
    <li className="item-content item-input">
      <div className="item-inner">
        <label className="item-title item-label">
          {label}
          <div className="item-input-wrap">
            <ReactPasswordStrength
              className="strength-input"
              scoreWords={[ 'Weak', 'Okay', 'Good', 'Strong', 'Very Strong' ]}
              tooShortWord='Weak'
              inputProps={{
                type,
                name,
                autoComplete: 'off',
                required,
                placeholder:  readOnly ? '' : placeholder,
                onBlur,
                onChange:     handleChange,
                onInput:      handleChange
              }}
            />
          </div>
        </label>
      </div>
    </li>
  );
}


// Helps with input fields (phone number for now).
export function inputAssist({ assistType, type, value, currency = 'USD' }) {
  assert(value !== null && value !== undefined, 'You forgot to initialize the value of this field');
  switch (assistType || type) {
    case 'tel': {
      const phoneNumber = Phone.parse(value, 'US');
      if (phoneNumber.phone && Phone.isValidNumber(phoneNumber)) {
        const format = phoneNumber.country === 'US' ? 'National' : 'International';
        return Phone.format(phoneNumber, format);
      } else {
        const formatter      = new Phone.AsYouType('US'); // eslint-disable-line new-cap
        const output         = formatter.input(value);
        const allowBackspace = output.replace(/[)\s]+$/g, '');
        return allowBackspace;
      }
    }
    case 'capitalize': {
      const parts = value.split(' ');
      return parts.map(capitalize).join(' ');
    }
    case 'money': {
      if (!value)
        return value;

      const options      = {
        style: 'currency',
        currency
      };
      const currentValue = value.replace(/\D/g, '') / 100;
      const formatted    = new Intl.NumberFormat('en-US', options).format(currentValue);

      return formatted;
    }
    default:
      return value;
  }
}


function capitalize(word) {
  return word
    .charAt(0)
    .toUpperCase()
    .concat(word.slice(1));
}


// Input field which allows user to select one or several options.
//
// name     - Field name
// value    - Current value
// label    - Label text
// options  - Available options
// ...form  - All other properties from the form
//
// Options can be one of:
// - Array of strings (value and label are the same)
// - Array of [value, label] pairs
// - Object with options: { value: label }
export function Select({ get, name, value, label, options, readOnly, onChange }) {
  const currentValue = value ? value : (get && get(name) || '');

  const containerClasses = classnames('form__select__container', 'item-input-wrap', { 'read-write': !readOnly });

  return (
    <li className="item-content item-input">
      <div className="item-inner">
        <label className="form__select item-label no-fastclick">
          {label}
          <div className={containerClasses}>
            <select
              name={name}
              value={currentValue}
              disabled={readOnly}
              onChange={onChange}>
              {renderSelectOptions(options)}
            </select>
          </div>
        </label>
      </div>
    </li>
  );
}


function renderSelectOptions(options) {
  return toValueLabelPairs(options)
    .map(([ value, label ]) => (
      <option key={value} value={value}>{label}</option>
    ));
}


function toValueLabelPairs(options) {
  if (Array.isArray(options))
    return options.map(option => (Array.isArray(option) ? option : [ option, option ]));
  else
    return Object.entries(options);
}


// Input field with a checkbox
//
// name     - Field name
// value    - Initial value
// label    - Label text
// ...form  - All other properties from the form
//
// TODO let's implement our own checkbox because F7's doesn't seem to work all
// that reliably
export function Checkbox({ name, label, children, get, readOnly, onChange, checked = false }) {
  const [ isChecked, setChecked ] = useState(checked);
  const value                     = get && get(name) || '';

  return (
    <li className="checkbox item-content item-input">
      <div className="item-inner">
        <label className="checkbox__label no-fastclick">
          <input
            id={name}
            name={name}
            type="checkbox"
            defaultValue={value}
            readOnly={readOnly}
            onClick={() => setChecked(!isChecked)}
            onChange={onChange}
            checked={isChecked}
          />
          <div className="box"></div>
          <div className="checkbox__label__labels">
            <div className="checkbox__label__labels_title">{label}</div>
          </div>
        </label>
      </div>
      {children}
    </li>
  );
}


// Radio field
// useRadioStyle - true/false - Framework 7 has two radio button styles, with checkbox or the standard radio.
export function Radio({ defaultChecked, useRadioStyle, children, label, subtitle, name, onChange, className, disabled, readOnly, value = '' }) {
  const classNames = classnames(
    'item-content',
    'no-fastclick',
    {
      'radio':      useRadioStyle,
      'item-radio': !useRadioStyle,
      disabled
    },
    className
  );

  function handleOnClick(event) {
    if (readOnly)
      event.preventDefault();
  }

  return (
    <li>
      <label className={classNames} onClick={handleOnClick}>
        <input
          type="radio"
          name={name}
          value={value}
          checked={defaultChecked}
          readOnly={readOnly}
          onChange={onChange}
        />
        <i className="icon icon-radio"></i>
        <div className="item-inner">
          <div className="item-title">{label}</div>
          {children}
          {subtitle && (<div className="item-subtitle">{subtitle}</div>)}
        </div>
      </label>
    </li>
  );
}


// Form button.  If the button is tied to the form, it will be enabled only when
// the form is valid and modified.  Otherwise, the button is always active.
export function Button({ label }) {
  return (
    <li className="form-button item-content">
      <div className="item-inner">
        <button className="button button-fill block">{label}</button>
      </div>
    </li>
  );
}


// Show error message at the top of the form (eg invalid credentials)
//
// This component has a fixed size, even when no message showing.
export function Error({ error }) {
  const isErrorList = Array.isArray(error);

  return (
    <li className="item-content item-input-invalid">
      {error && isErrorList && (
        <ul className="error-list">
          {error.map(err => (<li key={err} className="item-input-error-message">{err}</li>))}
        </ul>
      )}
      {error && !isErrorList && (
        <div className="item-input-error-message">{error}</div>
      )}
    </li>
  );
}


// Clear the error messages attached by fw7
function clearErrorMessages(target) {
  target.classList.remove('input-invalid');
  const parentInput = target.closest('.item-input');
  if (parentInput) {
    parentInput.classList.remove('item-input-invalid');
    parentInput.classList.remove('item-input-with-error-message');
  }
}

function newFormID(record) {
  const formID = (record && record.id) || parseInt(Math.random() * 100000, 10);
  return `form-${formID}`;
}


export function Field({ label, value }) {
  return (
    <li className="item-content item-input item-field">
      <div className="item-inner">
        <label className="item-title item-label">{label}</label>
        <div className="item-input-wrap">{value}</div>
      </div>
    </li>
  );
}
